import React, { useState, useEffect, useCallback } from 'react';
import { ServiceLog } from '../types';
import { getServiceLogs } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 20;

// LEVEL_OPTIONS built dynamically with i18n

// CATEGORY_OPTIONS built dynamically with i18n

const LEVEL_STYLES: Record<string, React.CSSProperties> = {
  fatal: { color: '#fff', background: '#dc2626' },
  error: { color: '#dc2626', background: 'rgba(220,38,38,0.1)' },
  warn:  { color: '#d97706', background: 'rgba(217,119,6,0.1)' },
  info:  { color: '#2563eb', background: 'rgba(37,99,235,0.1)' },
};

// CATEGORY_LABELS moved to i18n

function formatTime(s: string): string {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
};

const ServiceLogs: React.FC = () => {
  const { t } = useTranslation('profile');
  const LEVEL_OPTIONS = [
    { value: '', label: t('serviceLogs.allLevels') },
    { value: 'fatal', label: 'FATAL' },
    { value: 'error', label: 'ERROR' },
    { value: 'warn', label: 'WARN' },
    { value: 'info', label: 'INFO' },
  ];
  const CATEGORY_OPTIONS = [
    { value: '', label: t('serviceLogs.allCategories') },
    { value: 'startup', label: t('serviceLogs.catStartup') },
    { value: 'request', label: t('serviceLogs.catRequest') },
    { value: 'business', label: t('serviceLogs.catBusiness') },
    { value: 'external_api', label: t('serviceLogs.catExternalApi') },
    { value: 'worker', label: 'Worker' },
    { value: 'billing', label: t('serviceLogs.catBilling') },
    { value: 'reconciler', label: t('serviceLogs.catReconciler') },
    { value: 'poller', label: t('serviceLogs.catPoller') },
    { value: 'redis', label: 'Redis' },
    { value: 'recovery', label: t('serviceLogs.catRecovery') },
  ];
  const [logs, setLogs] = useState<ServiceLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const loadLogs = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const res = await getServiceLogs(p, PAGE_SIZE, level || undefined, category || undefined);
      setLogs(res.data || []);
      setTotal(res.total);
      setPage(p);
    } catch (err: unknown) {
      setLogs([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : t('serviceLogs.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [level, category, t]);

  useEffect(() => {
    loadLogs(1);
  }, [loadLogs]);

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
  };

  return (
    <div className="container">
      <Breadcrumb items={[{ label: t('serviceLogs.breadcrumbHome'), to: '/projects' }, { label: t('serviceLogs.breadcrumbTitle') }]} />

      <div className="page-header">
        <h1 className="page-title">{t('serviceLogs.pageTitle')}</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select className="input" value={level} onChange={handleFilterChange(setLevel)} style={{ width: 'auto', minWidth: '110px', fontSize: '0.85rem', padding: '0.35rem 0.5rem' }}>
            {LEVEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="input" value={category} onChange={handleFilterChange(setCategory)} style={{ width: 'auto', minWidth: '120px', fontSize: '0.85rem', padding: '0.35rem 0.5rem' }}>
            {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => loadLogs(page)} disabled={loading} title={t("serviceLogs.refreshBtn")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: 'rgba(220,38,38,0.06)', borderColor: 'var(--danger)', color: 'var(--danger)', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {loading && logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('serviceLogs.loading')}</div>
      ) : logs.length === 0 ? (
        <EmptyState icon="&#128220;" description={t("serviceLogs.emptyLogs")} />
      ) : (
        <div className="card" style={{ padding: 0, opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
              {t('serviceLogs.totalRecords', { total })}{totalPages > 1 && t('serviceLogs.pagination', { page, totalPages })}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ ...thStyle, width: '64px' }}>{t('serviceLogs.thLevel')}</th>
                  <th style={{ ...thStyle, width: '90px' }}>{t('serviceLogs.thCategory')}</th>
                  <th style={{ ...thStyle, width: '110px' }}>{t('serviceLogs.thSource')}</th>
                  <th style={thStyle}>{t('serviceLogs.thMessage')}</th>
                  <th style={{ ...thStyle, width: '140px' }}>{t('serviceLogs.thTime')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const ls = LEVEL_STYLES[log.level] || LEVEL_STYLES.info;
                  const isExpanded = expandedId === log.id;
                  const hasDetail = !!(log.detail || log.method || log.path || log.requestId || log.userId);
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => hasDetail && setExpandedId(isExpanded ? null : log.id)}
                        style={{
                          borderBottom: isExpanded ? 'none' : '1px solid var(--border)',
                          cursor: hasDetail ? 'pointer' : 'default',
                        }}
                      >
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block', padding: '0.15rem 0.45rem', borderRadius: '4px',
                            fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: '0.03em', ...ls,
                          }}>
                            {log.level}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>
                          {t('serviceLogs.cat' + log.category.charAt(0).toUpperCase() + log.category.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase()), { defaultValue: log.category })}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-3)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {log.source || '-'}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.message}
                          {hasDetail && (
                            <span style={{ color: 'var(--text-3)', marginLeft: '0.4rem', fontSize: '0.7rem' }}>
                              {isExpanded ? '▲' : '▼'}
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {formatTime(log.createdAt)}
                        </td>
                      </tr>
                      {isExpanded && hasDetail && (
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan={5} style={{ padding: '0 0.75rem 0.75rem', background: 'var(--surface-2, #f9fafb)' }}>
                            <div style={{
                              padding: '0.75rem',
                              borderRadius: 'var(--radius-sm, 6px)',
                              background: 'var(--surface, #fff)',
                              border: '1px solid var(--border)',
                            }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', fontSize: '0.8rem', marginBottom: log.detail ? '0.75rem' : 0 }}>
                                {log.method && (
                                  <DetailItem label={t("serviceLogs.detailRequest")} value={<strong>{log.method} {log.path}</strong>} />
                                )}
                                {log.status !== undefined && log.status > 0 && (
                                  <DetailItem label={t("serviceLogs.detailStatus")} value={
                                    <strong style={{ color: log.status >= 500 ? 'var(--danger)' : 'var(--warning, #d97706)' }}>{log.status}</strong>
                                  } />
                                )}
                                {log.duration !== undefined && log.duration > 0 && (
                                  <DetailItem label={t("serviceLogs.detailDuration")} value={`${log.duration}ms`} />
                                )}
                                {log.requestId && (
                                  <DetailItem label="RequestID" value={
                                    <code style={{ fontSize: '0.75rem', background: 'var(--surface-2, #f3f4f6)', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>{log.requestId}</code>
                                  } />
                                )}
                                {log.userId && (
                                  <DetailItem label={t("serviceLogs.detailUser")} value={log.userId} />
                                )}
                                {log.ip && (
                                  <DetailItem label="IP" value={log.ip} />
                                )}
                              </div>
                              {log.detail && (
                                <div style={{
                                  padding: '0.6rem 0.75rem', borderRadius: 'var(--radius-sm, 6px)',
                                  background: 'var(--surface-2, #f1f5f9)', fontFamily: 'monospace',
                                  fontSize: '0.75rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-all', maxHeight: '200px', overflow: 'auto',
                                  color: 'var(--text-2)', border: '1px solid var(--border)',
                                }}>
                                  {log.detail}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} loading={loading} onPageChange={loadLogs} />
          )}
        </div>
      )}
    </div>
  );
};

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span style={{ color: 'var(--text-3)', marginRight: '0.25rem' }}>{label}:</span>
      {value}
    </div>
  );
}

function Pagination({ page, totalPages, loading, onPageChange }: {
  page: number; totalPages: number; loading: boolean; onPageChange: (p: number) => void;
}) {
  const { t } = useTranslation("profile");
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: '0.5rem', padding: '0.75rem 1rem',
      borderTop: '1px solid var(--border)',
    }}>
      <button className="btn btn-ghost btn-sm" disabled={page <= 1 || loading} onClick={() => onPageChange(page - 1)}>
        {t('serviceLogs.prevPage')}
      </button>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>{page} / {totalPages}</span>
      <button className="btn btn-ghost btn-sm" disabled={page >= totalPages || loading} onClick={() => onPageChange(page + 1)}>
        {t('serviceLogs.nextPage')}
      </button>
    </div>
  );
}

export default ServiceLogs;
